/**
 * The words, colours and labels of a trade idea.
 *
 * Kept React-free and klinecharts-free on purpose: `customOverlays.ts` imports
 * the label builders to draw them on the canvas, and the overlay registry runs
 * outside any component.
 *
 * One vocabulary, one place. The prototype this is ported from ships two
 * spellings of the same concept — its refusal message says "Target" while every
 * other surface says "Take profit" — because the strings were written where
 * they were needed. Everything here is the single source.
 */

export type IdeaRole = 'entry' | 'sl' | 'tp'

export interface IdeaStep {
  key: IdeaRole
  label: string
  /** More than one may be placed (take profits: tp, tp2, tp3 …). */
  unlimited?: boolean
}

/**
 * The order the flow walks: risk before reward. A trader who has marked an
 * entry is asked what invalidates it before being asked what it pays.
 */
export const IDEA_STEPS: IdeaStep[] = [
  { key: 'entry', label: 'Entry' },
  { key: 'sl', label: 'Stop loss' },
  { key: 'tp', label: 'Take profit', unlimited: true },
]

/**
 * Idea identity colours, cycled by creation order: one palette per theme, slot
 * for slot (PROD-3134). The dark one is the prototype's, lifted so it reads on a
 * dark chart; the light one carries the same five hues deep enough to read on
 * white, where the dark set washes out.
 *
 * Deliberately excludes buy-green (#089981) and sell-red (#f23645) — an idea's
 * identity must never be mistaken for a direction — and yellow (#ffd60a), which
 * is DEFAULT_DRAW_COLOR in Workbench.tsx: a yellow idea would be
 * indistinguishable from an ordinary trend line.
 */
export const IDEA_COLORS_DARK = [
  '#66a0ff', 
  '#c77dd6', 
  '#ff9800', 
  '#22d3ee', 
  '#f472b6',
]
export const IDEA_COLORS_LIGHT = [
  '#2563eb',
  '#7c3aed', 
  '#d97706', 
  '#0891b2', 
  '#db2777', 
]
/** The single palette before PROD-3134, which ideas saved under it still carry. */
const IDEA_COLORS_LEGACY = ['#2970ff', '#9c27b0', '#ff9800', '#22d3ee', '#e91e63']

const palette = (isDark: boolean) => (isDark ? IDEA_COLORS_DARK : IDEA_COLORS_LIGHT)

/** Palette colour for a creation ordinal, in the given theme; wraps, and tolerates negatives. */
export function ideaColor(seq: number, isDark: boolean): string {
  const colors = palette(isDark)
  const n = colors.length
  const i = Number.isFinite(seq) ? Math.trunc(seq) : 0
  return colors[((i % n) + n) % n]
}


export function themedIdeaColor(color: string, isDark: boolean, picked = false): string {
  if (picked) return color
  const c = color.toLowerCase()
  for (const colors of [IDEA_COLORS_DARK, IDEA_COLORS_LIGHT, IDEA_COLORS_LEGACY]) {
    const i = colors.indexOf(c)
    if (i >= 0) return palette(isDark)[i]
  }
  return color
}

/** The handle a trader says out loud: "trade idea two, tighten the stop". */
export function ideaName(seq: number): string {
  return `Trade idea #${(Number.isFinite(seq) ? Math.trunc(seq) : 0) + 1}`
}

/** 'tp' | 'tp2' | 'tp13' → 'tp'. Null for anything unrecognised. */
export function roleOfSlot(slot: string | undefined): IdeaRole | null {
  const base = String(slot ?? '').replace(/\d+$/, '')
  return IDEA_STEPS.some((s) => s.key === base) ? (base as IdeaRole) : null
}

/** 'tp' → 1, 'tp2' → 2, 'tp13' → 13. The ordinal within its role. */
export function slotIndex(slot: string | undefined): number {
  const m = /(\d+)$/.exec(String(slot ?? ''))
  return m ? Number(m[1]) : 1
}

/** Sentence-case label for lists, the composer and hover cards. */
export function markLabel(role: IdeaRole, slot: string, dir: 'buy' | 'sell'): string {
  if (role === 'entry') return dir === 'sell' ? 'Sell entry' : 'Buy entry'
  const n = slotIndex(slot)
  const base = role === 'sl' ? 'Stop loss' : 'Take profit'
  return n > 1 ? `${base} ${n}` : base
}

/** Upper-case label drawn on the chart line itself. */
export function markLineLabel(role: IdeaRole, slot: string, dir: 'buy' | 'sell'): string {
  return markLabel(role, slot, dir).toUpperCase()
}

/** What the note box says it is attached to. */
export const NOTE_SUBJECT: Record<IdeaRole, string> = {
  entry: 'this entry',
  sl: 'this stop loss',
  tp: 'this take profit',
}

/**
 * The note box's header. It names the exact object the box is attached to, so it
 * never reads as a generic "talk to the AI" prompt.
 */
export function noteHeader(role: IdeaRole): string {
  return `Add more context about ${NOTE_SUBJECT[role]}`
}

/**
 * The question inside the field. Plain questions, not intake-speak — the same
 * three the prototype settled on.
 */
export const NOTE_QUESTION: Record<IdeaRole, string> = {
  entry: 'Why would you enter here?',
  sl: 'Why place your stop loss here?',
  tp: 'Why take profit here?',
}

export interface IdeaLook {
  /** Mirrors the shape the tradeMark overlay draws, so card and chart agree. */
  glyph: string
  tone: 'success' | 'danger'
  lead: string
}

/**
 * How a level presents itself on the hover card.
 *
 * Deliberately NOT `legTypes.LEG_TYPES` — see the warning at the top of that file.
 * Trade ideas own their words; the legacy tool owns its own.
 *
 * Only the entry needs the direction: a stop is a stop whichever way the trade
 * points, but "I'm buying here" and "I'm selling here" are different sentences.
 *
 * The voice is present tense — the trader is describing a plan that exists, not
 * weighing one up. That also keeps every string here distinct from the legacy
 * tool's conditional phrasing ("I'd sell here because…"), so identical copy can
 * never disguise the two vocabularies having been re-crossed.
 */
export function ideaLook(role: IdeaRole, dir: 'buy' | 'sell'): IdeaLook {
  if (role === 'sl') return { glyph: '✕', tone: 'danger', lead: 'My stop goes here because' }
  if (role === 'tp') return { glyph: '◆', tone: 'success', lead: "I'm taking profit here because" }
  return dir === 'sell'
    ? { glyph: '●', tone: 'danger', lead: "I'm selling here because" }
    : { glyph: '●', tone: 'success', lead: "I'm buying here because" }
}

/** Crosshair prompt while a step is armed. */
export const PLACE_PROMPT: Record<IdeaRole, string> = {
  entry: 'Click to mark your entry',
  sl: 'Click to mark your stop loss',
  tp: 'Click to mark your take profit',
}

/**
 * The same prompt for a screen with no pointer, where it is pinned to the trade-idea strip
 * rather than riding a crosshair. A phone has no
 * click, so it asks for a tap.
 */
export const TAP_PROMPT: Record<IdeaRole, string> = {
  entry: 'Tap to mark your entry',
  sl: 'Tap to mark your stop loss',
  tp: 'Tap to mark your take profit',
}

/** Longer form, for the strip and empty states. */
export const STEP_INSTRUCTION: Record<IdeaRole, string> = {
  entry: 'Click anywhere on the chart to mark your entry',
  sl: 'Click anywhere on the chart to mark your stop loss',
  tp: 'Click anywhere on the chart to mark your take profit',
}

export interface NoteFooter {
  primary: string
  /** True when the primary ADVANCES the flow; false when it FINISHES the idea. */
  advances: boolean
  /** The take profit's second button — another target, not a rival to saving. */
  addAnother: boolean
}

/**
 * The footer of a level's note box.
 *
 * **The primary button follows the IDEA, not the level.** While the idea is
 * still missing something it advances ("Next"); once it is missing nothing there
 * is nowhere to advance to, so it finishes ("Save trade idea"). The same stop
 * loss box therefore says "Next" while it is being built — a target is still
 * owed — and "Save trade idea" when it is reopened later to fix a sentence.
 *
 * Keying it off the role alone is what produced the reported bug: a reopened
 * stop loss still said "Next", and pressing it armed a take profit nobody asked
 * for. `next` comes from `pendingStep(idea, role)`, the single forward-only
 * lookup, so the button cannot promise a step the idea does not want.
 *
 * "Add another take profit" IS keyed off the role, because only targets
 * repeat — a trade has one way in and one place it is wrong — and it is offered
 * whether that target was just placed or clicked long afterwards.
 *
 * `Next` is bare on purpose: the progress strip above the chart already names
 * the steps, so "Next: Take profit" would only say it twice.
 */
export function noteFooter(role: IdeaRole, next: IdeaRole | null): NoteFooter {
  return {
    primary: next ? 'Next' : 'Save trade idea',
    advances: next != null,
    addAnother: role === 'tp',
  }
}
